import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  type ApiError,
  type AttributionReportResult,
  type CampaignAttributionResult,
} from '@whinerp/client';
import { formatCount, formatMoney } from '@whinerp/contracts/format';
import { Button, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Attribution, two models at once (F-080).
 *
 * The UX pointer is the default: "show two models at once. A single
 * attribution number presented as truth is misleading." There is no one-model
 * view to fall back to. Under the table, what the report covers and what it
 * left out - so its total can be put beside the won revenue and reconciled.
 */

const MODELS = ['first', 'last', 'linear', 'u-shaped', 'time-decay'] as const;
type Model = (typeof MODELS)[number];
const DIMENSIONS = ['utm_campaign', 'utm_source', 'utm_medium'] as const;
type Dimension = (typeof DIMENSIONS)[number];
type Report = AttributionReportResult;

const SELECT = 'h-input rounded-control border border-control bg-surface px-2 text-dense text-ink';

/**
 * The period starts unset, and the server reports the one it used.
 *
 * Working "today" out here gave the reader's day, which is not the
 * workspace's for part of every day - a deal won this morning in Dubai fell
 * outside "the last 90 days" read from a browser still on yesterday (D-354).
 */
function usePeriod() {
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [modelA, setModelA] = useState<Model>('first');
  const [modelB, setModelB] = useState<Model>('linear');
  return { from, setFrom, to, setTo, modelA, setModelA, modelB, setModelB };
}

function Controls({ period, used, dimension, setDimension }: {
  period: ReturnType<typeof usePeriod>;
  /** The period the last answer covered, shown until somebody picks one. */
  used: { from: string; to: string } | null;
  dimension?: Dimension;
  setDimension?: (value: Dimension) => void;
}): JSX.Element {
  const t = useT();
  const model = (value: Model, set: (value: Model) => void, cell: string) => (
    <label className="flex flex-col gap-1 text-dense text-ink-600">
      {t(cell === 'a' ? 'attribution.modelA' : 'attribution.modelB')}
      <select data-attribution-model={cell} className={SELECT} value={value} onChange={(event) => set(event.target.value as Model)}>
        {MODELS.map((entry) => (
          <option key={entry} value={entry}>
            {t(`attribution.model.${entry}` as 'attribution.model.first')}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <div className="flex flex-wrap items-end gap-2">
      {model(period.modelA, period.setModelA, 'a')}
      {model(period.modelB, period.setModelB, 'b')}
      {dimension && setDimension ? (
        <label className="flex flex-col gap-1 text-dense text-ink-600">
          {t('attribution.dimension')}
          <select data-attribution-dimension className={SELECT} value={dimension} onChange={(event) => setDimension(event.target.value as Dimension)}>
            {DIMENSIONS.map((entry) => (
              <option key={entry} value={entry}>
                {entry}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="flex flex-col gap-1 text-dense text-ink-600">
        {t('attribution.from')}
        <input data-attribution-from type="date" className={SELECT} value={period.from ?? used?.from ?? ''} onChange={(event) => period.setFrom(event.target.value || null)} />
      </label>
      <label className="flex flex-col gap-1 text-dense text-ink-600">
        {t('attribution.to')}
        <input data-attribution-to type="date" className={SELECT} value={period.to ?? used?.to ?? ''} onChange={(event) => period.setTo(event.target.value || null)} />
      </label>
    </div>
  );
}

function Table({ report }: { report: Report }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const money = (amount: string) => formatMoney(amount, format, { currency: report.currency });
  // The contract publishes the pair as an array; the server always sends two.
  const name = (model: Model | undefined) => (model ? t(`attribution.model.${model}` as 'attribution.model.first') : '');
  return (
    <div className="flex flex-col gap-2">
      {report.rows.length === 0 ? (
        <p data-attribution-none className="text-meta text-ink-meta">{t('attribution.none')}</p>
      ) : (
        <div className="w-full overflow-x-auto" data-scroll-container>
          <table className="w-full min-w-panel-narrow border-collapse" data-attribution-table>
            <thead>
              <tr className="border-b border-b-rule-strong">
                <th scope="col" className="px-2 py-1 text-start text-micro uppercase tracking-label text-ink-label">{report.dimension}</th>
                <th scope="col" className="px-2 py-1 text-end text-micro uppercase tracking-label text-ink-label">{name(report.models[0])}</th>
                <th scope="col" className="px-2 py-1 text-end text-micro uppercase tracking-label text-ink-label">{name(report.models[1])}</th>
                <th scope="col" className="px-2 py-1 text-end text-micro uppercase tracking-label text-ink-label">{t('attribution.deals')}</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row) => (
                <tr key={row.key ?? ''} data-attribution-row={row.key ?? ''} className="border-b border-b-rule">
                  <td className="px-2 py-1 text-dense text-ink">
                    {/* The raw value, always; the campaign it belongs to beside it. */}
                    <code className="text-dense">{row.key ?? t('attribution.noValue')}</code>
                    {row.campaignId ? (
                      <Link to={`/campaigns/${row.campaignId}`} className="ms-2 text-meta text-bronze-text hover:text-ink">
                        {row.campaignName}
                      </Link>
                    ) : null}
                  </td>
                  <td className="px-2 py-1 text-end"><Num align="end" data-credit-a>{money(row.credit[0] ?? '0')}</Num></td>
                  <td className="px-2 py-1 text-end"><Num align="end" data-credit-b>{money(row.credit[1] ?? '0')}</Num></td>
                  <td className="px-2 py-1 text-end"><Num align="end">{formatCount(row.deals, format)}</Num></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p data-attribution-covers className="text-meta text-ink-meta">
        {t('attribution.covers', {
          values: {
            deals: formatCount(report.covers.attributedDeals, format),
            revenue: money(report.covers.attributedRevenue),
            unattributed: formatCount(report.covers.unattributedDeals, format),
            unattributedRevenue: money(report.covers.unattributedRevenue),
            other: formatCount(report.covers.otherCurrencyDeals, format),
          },
        })}
      </p>
    </div>
  );
}

/** The workspace's report, on the campaigns list. */
export function AttributionPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const enabled = useEntitlement('F-080');
  const period = usePeriod();
  const [dimension, setDimension] = useState<Dimension>('utm_campaign');
  const sameModel = period.modelA === period.modelB;
  const report = useQuery<Report, ApiError>({
    queryKey: ['attribution', period.modelA, period.modelB, dimension, period.from, period.to],
    queryFn: () =>
      api.attributionReport({
        query: {
          modelA: period.modelA,
          modelB: period.modelB,
          dimension,
          ...(period.from ? { from: period.from } : {}),
          ...(period.to ? { to: period.to } : {}),
        },
      }),
    enabled: enabled.enabled && !sameModel,
    placeholderData: keepPreviousData,
  });
  if (!enabled.enabled) return null;
  return (
    <Panel data-region="attribution">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('attribution.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('attribution.body')}</p>
        <Controls
          period={period}
          used={report.data ? { from: report.data.from, to: report.data.to } : null}
          dimension={dimension}
          setDimension={setDimension}
        />
        {sameModel ? (
          <p data-attribution-same className="text-dense text-warning-text">{t('refusal.attribution.sameModel')}</p>
        ) : report.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : report.isError ? (
          <p className="text-dense text-danger-text">{errorText(report.error)}</p>
        ) : (
          <Table report={report.data} />
        )}
      </div>
    </Panel>
  );
}

/** One campaign's attribution, through its tracking code, on the campaign record. */
export function CampaignAttributionPanel({ campaignId }: { campaignId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-080');
  const period = usePeriod();
  const [code, setCode] = useState<string | null>(null);
  const sameModel = period.modelA === period.modelB;
  const report = useQuery<CampaignAttributionResult, ApiError>({
    queryKey: ['campaign-attribution', campaignId, period.modelA, period.modelB, period.from, period.to],
    queryFn: () =>
      api.campaignAttribution({
        path: { campaignId },
        query: {
          modelA: period.modelA,
          modelB: period.modelB,
          ...(period.from ? { from: period.from } : {}),
          ...(period.to ? { to: period.to } : {}),
        },
      }),
    enabled: enabled.enabled && !sameModel,
    placeholderData: keepPreviousData,
  });
  const save = useMutation<unknown, ApiError>({
    mutationFn: () => api.setCampaignTrackingCode({ path: { campaignId }, body: { code: code ?? '' } }),
    onSuccess: async () => {
      setCode(null);
      await queryClient.invalidateQueries({ queryKey: ['campaign-attribution', campaignId] });
    },
  });
  if (!enabled.enabled) return null;
  const current = report.data?.trackingCode ?? '';
  return (
    <Panel data-region="campaign-attribution">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('attribution.campaignTitle')}</SectionLabel>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense text-ink-600">
            {t('attribution.trackingCode')}
            <input
              data-tracking-code
              className="h-input rounded-control border border-control bg-surface px-3 font-mono text-body text-ink"
              value={code ?? current}
              onChange={(event) => setCode(event.target.value)}
            />
          </label>
          <Button intent="secondary" size="small" data-tracking-code-save disabled={code === null || save.isPending} onClick={() => save.mutate()}>
            {t('attribution.saveCode')}
          </Button>
        </div>
        <p className="text-meta text-ink-meta">{t('attribution.trackingHelp')}</p>
        {save.error ? <p data-tracking-code-refusal className="text-dense text-danger-text">{errorText(save.error)}</p> : null}
        <Controls
          period={period}
          used={report.data?.report ? { from: report.data.report.from, to: report.data.report.to } : null}
        />
        {sameModel ? (
          <p className="text-dense text-warning-text">{t('refusal.attribution.sameModel')}</p>
        ) : report.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : report.isError ? (
          <p className="text-dense text-danger-text">{errorText(report.error)}</p>
        ) : report.data.report ? (
          <Table report={report.data.report} />
        ) : (
          <p data-attribution-no-code className="text-meta text-ink-meta">{t('attribution.noCode')}</p>
        )}
      </div>
    </Panel>
  );
}
